import { toast } from "sonner";
import { Component, useEffect, useRef, useState } from "react";
import { FileText, StickyNote, CalendarDays, Quote, Map as MapIcon, Clock, GitFork, Network, BookA, Clapperboard, GripVertical, ChevronDown, ChevronRight, Plus, X, PanelRightClose, PanelRightOpen, PanelLeftClose, PanelLeftOpen, ArrowLeftRight, NotebookPen, Music2, Feather, Info } from "lucide-react";
import { PoetryWidget } from "@/components/poetry/PoetryWidget";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { NoteWidget } from "@/components/studio/widgets/NoteWidget";
import { CalendarWidget, TimelineWidget, DictionaryWidget, QuotesWidget } from "@/components/studio/widgets/MiniWidgets";
import { MapWidget, RelationsWidget } from "@/components/studio/widgets/VisualWidgets";
import { QuickNoteForm } from "@/components/studio/widgets/QuickNoteForm";
import { SceneWidget } from "@/components/studio/widgets/SceneWidget";
import { MusicWidget } from "@/components/music/MusicWidget";
import { PresetMenu, usePresets } from "@/components/studio/widgets/PresetMenu";

const TYPES = {
  chapter: ["Chapter", FileText], note: ["Note", StickyNote], scene: ["Scene notes", Clapperboard], calendar: ["Calendar", CalendarDays], quotes: ["Quote catalog", Quote], map: ["Map", MapIcon],
  timeline: ["Timeline", Clock], tree: ["Family tree", GitFork], web: ["Relationship web", Network], dictionary: ["Dictionary", BookA], music: ["Music Player", Music2], poetry: ["Poetry & rhymes", Feather], info: ["Chapter info", Info],
};
const MAX = 6;
const newKey = () => `${Date.now()}${Math.random().toString(36).slice(2, 7)}`;
const load = (k) => {
  try {
    const c = JSON.parse(localStorage.getItem(k));
    return c ? { ...c, slots: (c.slots || []).map((s) => ({ ...s, k: s.k || newKey() })) } : null;
  } catch { return null; }
};

const Body = ({ slot, uid, sid, set, onInsert, chapterTools, chapterInfo, getSceneText }) => {
  const p = { uid, cfg: slot, set, onInsert };
  switch (slot.type) {
    case "chapter": return chapterTools || null;
    case "info": return chapterInfo || <p className="py-6 text-center text-xs text-[#A8A090]">Open a chapter to see its info.</p>;
    case "scene": return <SceneWidget {...p} />;
    case "calendar": return <CalendarWidget {...p} />;
    case "quotes": return <QuotesWidget {...p} />;
    case "map": return <MapWidget {...p} />;
    case "timeline": return <TimelineWidget {...p} />;
    case "tree": return <RelationsWidget uid={uid} mode="tree" />;
    case "web": return <RelationsWidget uid={uid} mode="web" />;
    case "dictionary": return <DictionaryWidget {...p} />;
    case "music": return <MusicWidget sid={sid} getSceneText={getSceneText} />;
    case "poetry": return <PoetryWidget onInsert={onInsert} />;
    default: return <NoteWidget {...p} />;
  }
};

const DRAG = "application/x-lf-widget";

class WidgetBoundary extends Component {
  constructor(p) { super(p); this.state = { err: false }; }
  static getDerivedStateFromError() { return { err: true }; }
  render() {
    if (this.state.err) return <p className="py-6 text-center text-xs text-[#A8A090]" data-testid="widget-error">This widget hit a problem. <button className="text-[#F59E0B] underline" onClick={() => this.setState({ err: false })}>Reload widget</button></p>;
    return this.props.children;
  }
}
const MIN_H = 120;

const startDrag = (e, cursor, onMove) => {
  e.preventDefault();
  const x0 = e.clientX, y0 = e.clientY;
  const move = (ev) => onMove(ev.clientX - x0, ev.clientY - y0);
  const up = () => { document.removeEventListener("pointermove", move); document.removeEventListener("pointerup", up); document.removeEventListener("pointercancel", up); window.removeEventListener("blur", up); document.body.style.cursor = ""; document.body.style.userSelect = ""; };
  document.body.style.cursor = cursor;
  document.body.style.userSelect = "none";
  document.addEventListener("pointermove", move);
  document.addEventListener("pointerup", up);
  document.addEventListener("pointercancel", up);
  window.addEventListener("blur", up);
};

const Handle = ({ dir, onPointerDown, onDoubleClick, testid, className, style }) => (
  <div style={style} onPointerDown={onPointerDown} onDoubleClick={onDoubleClick} title="Drag to resize · double-click to reset" data-testid={testid}
    className={`group flex shrink-0 items-center justify-center ${dir === "x" ? "cursor-col-resize" : "cursor-row-resize"} ${className}`}>
    <span className={`rounded-full bg-[#2D3442] transition-colors group-hover:bg-[#F59E0B] ${dir === "x" ? "h-12 w-1" : "h-1 w-12"}`} />
  </div>
);

const Slot = ({ slot, i, pos, uid, sid, getSceneText, update, remove, onInsert, onMove, count, dragRef, chapterTools, chapterInfo, chapterTaken }) => {
  const Icon = TYPES[slot.type]?.[1] || StickyNote;
  const [grab, setGrab] = useState(false);
  const [over, setOver] = useState(false);
  useEffect(() => {
    if (!grab) return undefined;
    const up = () => setGrab(false);
    document.addEventListener("mouseup", up);
    return () => document.removeEventListener("mouseup", up);
  }, [grab]);
  const ours = (e) => dragRef.current !== null && e.dataTransfer.types.includes(DRAG);
  return (
    <section draggable={grab} data-testid={`widget-slot-${i}`}
      onDragStart={(e) => { if (!grab || e.target !== e.currentTarget) return; dragRef.current = i; e.dataTransfer.effectAllowed = "move"; e.dataTransfer.setData(DRAG, String(i)); }}
      onDragEnd={() => { dragRef.current = null; setGrab(false); setOver(false); }}
      onDragOver={(e) => { if (ours(e)) { e.preventDefault(); setOver(dragRef.current !== i); } }}
      onDragLeave={() => setOver(false)}
      onDrop={(e) => { if (!ours(e)) return; e.preventDefault(); e.stopPropagation(); setOver(false); const r = e.currentTarget.getBoundingClientRect(); if (dragRef.current !== i) onMove(dragRef.current, i, e.clientY > r.top + r.height / 2); dragRef.current = null; }}
      style={{ order: pos * 2, flex: slot.collapsed ? "0 0 auto" : `${slot.grow || 1} 1 0px` }}
      className={`flex flex-col rounded-md border bg-[#13171F] transition-colors ${slot.collapsed ? "" : "min-h-[120px]"} ${over ? "border-[#F59E0B] ring-2 ring-[#D97706]/40" : "border-[#2D3442]"}`}>
      <header className={`flex items-center gap-1.5 px-2 py-1.5 ${slot.collapsed ? "" : "border-b border-[#2D3442]"}`}>
        <button onClick={() => update({ collapsed: !slot.collapsed })} title={slot.collapsed ? "Expand widget" : "Collapse widget"} className="rounded p-0.5 text-[#A8A090] hover:bg-[#1A202C] hover:text-[#F3EBDD]" data-testid={`widget-collapse-${i}`}>
          {slot.collapsed ? <ChevronRight className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
        </button>
        {count > 1 && (
          <span onMouseDown={() => setGrab(true)} onMouseUp={() => setGrab(false)} title="Drag to reorder" className="cursor-grab rounded p-0.5 text-[#A8A090]/70 hover:bg-[#1A202C] hover:text-[#F3EBDD] active:cursor-grabbing" data-testid={`widget-drag-${i}`}>
            <GripVertical className="h-3.5 w-3.5" />
          </span>
        )}
        <Icon className="h-3.5 w-3.5 shrink-0 text-[#F59E0B]" />
        <Select value={slot.type} onValueChange={(type) => update({ type, id: null })}>
          <SelectTrigger className="h-7 border-0 bg-transparent px-1 text-xs font-medium shadow-none" data-testid={`widget-type-${i}`}><SelectValue /></SelectTrigger>
          <SelectContent>{Object.entries(TYPES).map(([k, [l]]) => <SelectItem key={k} value={k} disabled={k === "chapter" && (chapterTaken || !chapterTools)} data-testid={`widget-type-option-${k}`}>{l}</SelectItem>)}</SelectContent>
        </Select>
        <button onClick={remove} title="Remove widget" className="ml-auto rounded p-1 text-[#A8A090] hover:bg-[#1A202C] hover:text-[#F87171]" data-testid={`widget-remove-${i}`}><X className="h-3.5 w-3.5" /></button>
      </header>
      {!slot.collapsed && (
        <div className="min-h-0 flex-1 overflow-y-auto p-2.5 lf-scroll" data-testid={`widget-body-${i}`}>
          <WidgetBoundary><Body slot={slot} uid={uid} sid={sid} getSceneText={getSceneText} set={(patch) => update(patch)} onInsert={onInsert} chapterTools={chapterTools} chapterInfo={chapterInfo} /></WidgetBoundary>
        </div>
      )}
    </section>
  );
};

const COLS0 = [{ side: "right", open: true }, { side: "right", open: true }];
const migrate = (c) => (c.cols ? c : { ...c, cols: [{ side: "right", open: c.open !== false, ...(c.width ? { width: c.width } : {}) }, { side: "right", open: true }] });

const Column = ({ c, cfg, setCfg, colSlots, slots, setSlots, add, uid, sid, getSceneText, onInsert, chapterTools, chapterInfo, dragRef, dragging, presetBits }) => {
  const col = cfg.cols[c];
  const listRef = useRef(null);
  const asideRef = useRef(null);
  const [narrow, setNarrow] = useState(false);
  const [quick, setQuick] = useState(false);
  const [over, setOver] = useState(false);
  const left = col.side === "left";
  const primary = c === (slots.some((s) => !(s.col || 0)) || !slots.length ? 0 : 1);
  const setCol = (patch) => setCfg((x) => ({ ...x, cols: x.cols.map((k, j) => (j === c ? { ...k, ...patch } : k)) }));
  useEffect(() => {
    if (!col.open || !asideRef.current) return undefined;
    const ro = new ResizeObserver(([e]) => setNarrow(e.contentRect.width < 380));
    ro.observe(asideRef.current);
    return () => ro.disconnect();
  }, [col.open]);
  const resizeWidth = (e) => {
    const w0 = asideRef.current.offsetWidth;
    startDrag(e, "col-resize", (dx) => setCol({ width: Math.round(Math.min(Math.max(260, window.innerWidth * 0.4), Math.max(260, w0 + (left ? dx : -dx)))) }));
  };
  const resizeSplit = (e, a, b) => {
    const el = (j) => listRef.current.querySelector(`[data-testid="widget-slot-${j}"]`);
    const h1 = el(a).offsetHeight, h2 = el(b).offsetHeight;
    const g = (slots[a].grow || 1) + (slots[b].grow || 1);
    startDrag(e, "row-resize", (_, dy) => {
      const nh = Math.min(Math.max(MIN_H, h1 + dy), h1 + h2 - MIN_H);
      const g1 = (g * nh) / (h1 + h2);
      setSlots((ss) => ss.map((x, j) => (j === a ? { ...x, grow: g1 } : j === b ? { ...x, grow: g - g1 } : x)));
    });
  };
  const ours = (e) => dragRef.current !== null && e.dataTransfer.types.includes(DRAG);
  const dropHere = (e) => { if (!ours(e)) return; e.preventDefault(); setOver(false); const from = dragRef.current; dragRef.current = null; setSlots((ss) => { const next = [...ss]; const [m] = next.splice(from, 1); return [...next, { ...m, col: c }]; }); };
  const order = left ? -10 + c : 10 + c;
  if (!colSlots.length && (c === 1 || slots.length)) {
    return dragging ? (
      <div style={{ order }} onDragOver={(e) => { if (ours(e)) { e.preventDefault(); setOver(true); } }} onDragLeave={() => setOver(false)} onDrop={dropHere}
        className={`sticky top-4 flex h-[calc(100vh-10rem)] w-24 shrink-0 items-center justify-center rounded-md border-2 border-dashed text-center text-[11px] ${over ? "border-[#F59E0B] bg-[#D97706]/10 text-[#FBBF24]" : "border-[#2D3442] text-[#A8A090]"}`} data-testid="widget-col-new-drop">
        Drop here for a second column
      </div>
    ) : null;
  }
  if (!col.open) {
    const Icon = left ? PanelLeftOpen : PanelRightOpen;
    return (
      <button style={{ order }} onClick={() => setCol({ open: true })} className="sticky top-4 flex h-fit shrink-0 flex-col items-center gap-2 rounded-md border border-[#2D3442] bg-[#13171F] px-2 py-3 text-[10px] uppercase tracking-wider text-[#A8A090] hover:border-[#D97706]/60 hover:text-[#F3EBDD]" data-testid={`widget-panel-open${c ? "-2" : ""}`}>
        <Icon className="h-4 w-4" /><span className="[writing-mode:vertical-rl]">Side panel{colSlots.length ? ` · ${colSlots.length}` : ""}</span>
      </button>
    );
  }
  const two = cfg.cols.every((k) => k.open) && slots.some((s) => (s.col || 0) === 1) && slots.some((s) => !(s.col || 0));
  const Hide = left ? PanelLeftClose : PanelRightClose;
  return (
    <aside ref={asideRef} style={{ order, ...(col.width ? { width: `min(${col.width}px, 40vw)`, minWidth: 260 } : {}) }}
      onDragOver={(e) => { if (ours(e)) e.preventDefault(); }} onDrop={dropHere}
      className={`sticky top-4 flex h-[calc(100vh-10rem)] shrink-0 flex-col gap-2.5 ${col.width ? "" : two ? "w-[260px] min-[1600px]:w-[320px] 2xl:w-[360px]" : "w-[380px] 2xl:w-[420px]"}`} data-testid={`widget-panel${c ? "-2" : ""}`}>
      <Handle dir="x" onPointerDown={resizeWidth} onDoubleClick={() => setCol({ width: undefined })} testid={`widget-panel-resize${c ? "-2" : ""}`} className={`absolute top-0 h-full w-3 ${left ? "-right-4" : "-left-4"}`} />
      <div className="flex items-center gap-1.5">
        <span className={`lf-label mr-auto min-w-0 truncate ${narrow && primary ? "invisible" : ""}`}>{!two ? "Side panel" : cfg.cols[0].side === cfg.cols[1].side ? `Panel ${c + 1}` : left ? "Left panel" : "Right panel"}</span>
        {primary && <button onClick={() => setQuick(!quick)} title="Quick note" className="flex shrink-0 items-center gap-1 whitespace-nowrap rounded-full border border-[#D97706]/50 px-2.5 py-1 text-[11px] text-[#F59E0B] hover:bg-[#D97706]/10" data-testid="widget-quick-note-button"><NotebookPen className="h-3.5 w-3.5" />{!narrow && "Quick note"}</button>}
        {primary && <PresetMenu uid={uid} snapshot={presetBits.snapshot} onApply={presetBits.apply} compact={narrow} />}
        <button onClick={() => add({ type: "note", id: null, col: c })} disabled={slots.length >= MAX} title={slots.length >= MAX ? `Up to ${MAX} widgets` : "Add a widget"} className="flex shrink-0 items-center gap-1 whitespace-nowrap rounded-full border border-[#2D3442] px-2.5 py-1 text-[11px] text-[#E8DFD1] hover:border-[#D97706]/60 disabled:opacity-40" data-testid={`widget-add-button${c ? "-2" : ""}`}><Plus className="h-3.5 w-3.5" />{!narrow && "Widget"}</button>
        <button onClick={() => setCol({ side: left ? "right" : "left" })} title={`Move this column to the ${left ? "right" : "left"} of the page`} className="rounded p-1 text-[#A8A090] hover:text-[#F3EBDD]" data-testid={`widget-col-side${c ? "-2" : ""}`}><ArrowLeftRight className="h-4 w-4" /></button>
        <button onClick={() => setCol({ open: false })} title="Hide this panel" className="rounded p-1 text-[#A8A090] hover:text-[#F3EBDD]" data-testid={`widget-panel-close${c ? "-2" : ""}`}><Hide className="h-4 w-4" /></button>
      </div>
      {quick && <QuickNoteForm uid={uid} onClose={() => setQuick(false)} onCreated={(e) => { setQuick(false); add({ type: "note", id: e.id, col: c }); }} />}
      <div ref={listRef} className="flex min-h-0 flex-1 flex-col" data-testid={`widget-list${c ? "-2" : ""}`}>
        {colSlots.slice(1).map((s, j) => {
          const a = slots.indexOf(colSlots[j]), b = slots.indexOf(s);
          return !colSlots[j].collapsed && !s.collapsed
            ? <Handle key={`h${j}`} dir="y" style={{ order: j * 2 + 1 }} onPointerDown={(e) => resizeSplit(e, a, b)} onDoubleClick={() => setSlots((ss) => ss.map((x, k) => (k === a || k === b ? { ...x, grow: 1 } : x)))} testid={`widget-split-${a}`} className="h-2.5 w-full" />
            : <div key={`h${j}`} style={{ order: j * 2 + 1 }} className="h-2.5 shrink-0" />;
        })}
        {[...colSlots].sort((x, y) => (x.k < y.k ? -1 : 1)).map((s) => {
          const i = slots.indexOf(s);
          return (
            <Slot key={s.k} i={i} pos={colSlots.indexOf(s)} slot={s} uid={uid} sid={sid} getSceneText={getSceneText} onInsert={onInsert} count={slots.length} dragRef={dragRef} chapterTools={chapterTools} chapterInfo={chapterInfo} chapterTaken={s.type !== "chapter" && slots.some((x) => x.type === "chapter")}
              onMove={(from, to, after) => setSlots((ss) => { const next = [...ss]; const [m] = next.splice(from, 1); const t = next.indexOf(ss[to]); next.splice(after ? t + 1 : t, 0, { ...m, col: c }); return next; })}
              update={(patch) => setSlots((ss) => ss.map((x) => (x.k === s.k ? { ...x, ...patch } : x)))}
              remove={() => setSlots((ss) => ss.filter((x) => x.k !== s.k))} />
          );
        })}
      </div>
      {!colSlots.length && <button onClick={() => add({ type: "note", id: null, col: c })} className="flex flex-1 items-center justify-center rounded-md border border-dashed border-[#2D3442] text-sm text-[#A8A090] hover:border-[#D97706]/60" data-testid={`widget-empty-add${c ? "-2" : ""}`}>Add a widget</button>}
    </aside>
  );
};

export const WidgetPanel = ({ sid, uid, getSceneText, onInsert, chapterTools, chapterInfo, onChapterSlot, apiRef }) => {
  const key = `lf_studio_widgets_${sid}`;
  const [cfg, setCfg] = useState(() => { const c = load(key); return c ? migrate(c) : { cols: COLS0, fresh: true, slots: [{ type: "note", id: null, k: newKey() }] }; });
  const { data: presets } = usePresets();
  const applyPreset = (p) => setCfg((c) => {
    const slots = p.layout.slots.map((s) => ({ type: s.type, id: p.universe_id === uid ? s.id || null : null, grow: s.grow, collapsed: !!s.collapsed, col: s.col || 0, k: newKey() }));
    const cols = p.layout.cols ? p.layout.cols.map((k) => ({ side: k.side, open: true, ...(k.width ? { width: k.width } : {}) })) : [{ side: "right", open: true, ...(p.layout.width ? { width: p.layout.width } : {}) }, { side: "right", open: true }];
    return { ...c, fresh: false, slots, cols: cols.length === 2 ? cols : [...cols, { side: "right", open: true }] };
  });
  const snapshot = () => ({ cols: cfg.cols.map((k) => ({ side: k.side, ...(k.width ? { width: k.width } : {}) })), slots: cfg.slots.map((s) => ({ type: s.type, id: s.id || null, ...(s.grow ? { grow: s.grow } : {}), collapsed: !!s.collapsed, col: s.col || 0 })) });
  useEffect(() => {
    const d = cfg.fresh && presets?.find((p) => p.is_default);
    if (d) applyPreset(d);
    else if (cfg.fresh && presets) setCfg((c) => ({ ...c, fresh: false }));
  }, [presets]); // eslint-disable-line react-hooks/exhaustive-deps
  const dragRef = useRef(null);
  const [dragging, setDragging] = useState(false);
  useEffect(() => { localStorage.setItem(key, JSON.stringify(cfg)); }, [key, cfg]);
  const slots = cfg.slots;
  const visible = (s) => cfg.cols[s.col || 0].open;
  const hasChapter = slots.some((s) => s.type === "chapter" && visible(s));
  useEffect(() => { onChapterSlot?.(hasChapter); }, [hasChapter]); // eslint-disable-line react-hooks/exhaustive-deps
  const openCol = (c, col) => ({ ...c, cols: c.cols.map((k, j) => (j === col ? { ...k, open: true } : k)) });
  const insert = (type, front, grow) => setCfg((c) => {
    const have = c.slots.find((s) => s.type === type);
    if (have) return openCol(c, have.col || 0);
    const rest = c.slots.length >= MAX ? (front ? c.slots.slice(0, MAX - 1) : c.slots.slice(0, MAX - 1)) : c.slots;
    const s = { type, id: null, k: newKey(), col: 0, ...(grow ? { grow } : {}) };
    return openCol({ ...c, fresh: false, slots: front ? [s, ...rest] : [...rest.map((x) => ((x.col || 0) === 0 && grow ? { ...x, grow: 1 } : x)), s] }, 0);
  });
  if (apiRef) apiRef.current = {
    addChapter: () => {
      if (!cfg.slots.some((s) => s.type === "chapter") && cfg.slots.length >= MAX) toast.info(`Replaced the ${TYPES[cfg.slots[MAX - 1].type]?.[0] || "last"} widget with the chapter details`);
      insert("chapter", true);
    },
    removeChapter: () => setCfg((c) => ({ ...c, slots: c.slots.filter((s) => s.type !== "chapter") })),
    addPoetry: () => insert("poetry", false, 2),
  };
  const setSlots = (fn) => setCfg((c) => ({ ...c, fresh: false, slots: fn(c.slots) }));
  const add = (slot = { type: "note", id: null }) => setSlots((ss) => (ss.length < MAX ? [...ss, { ...slot, k: newKey() }] : ss));
  const presetBits = { snapshot, apply: applyPreset };
  return (
    <div className="contents" onDragStart={() => setDragging(true)} onDragEnd={() => setDragging(false)} onDrop={() => setDragging(false)}>
      {[0, 1].map((c) => (
        <Column key={c} c={c} cfg={cfg} setCfg={setCfg} colSlots={slots.filter((s) => (s.col || 0) === c)} slots={slots} setSlots={setSlots} add={add}
          uid={uid} sid={sid} getSceneText={getSceneText} onInsert={onInsert} chapterTools={chapterTools} chapterInfo={chapterInfo} dragRef={dragRef} dragging={dragging && slots.length > 1} presetBits={presetBits} />
      ))}
    </div>
  );
};
